'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useI18n } from './i18n'
import {
  NOTIFICATION_SOURCE_LIST,
  buildNotificationDescriptor,
  normalizeNotificationCounts,
  normalizeNotificationSource,
  totalNotificationCounts,
} from '../lib/notificationCenter'
import { createNotificationImpulseLeader } from '../lib/notificationImpulseLeader'

const SOURCE_EVENT = 'ql7:notification-count'
const SERVICE_WORKER_URL = '/ql7-notification-sw.js'
const STORAGE_KEY = 'ql7_notification_counts_v1'
const READ_AT_STORAGE_KEY = 'ql7_notification_read_at_v1'
const NATIVE_LINK_STORAGE_KEY = 'ql7_native_push_linked_account_v1'
const PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY = 'ql7_push_subscription_fingerprint_v1'
const NOTICE_PARAM = 'ql7Notice'
const NATIVE_LINK_RETRY_MS = 10 * 60 * 1000
const PUSH_SUBSCRIPTION_REGISTER_TTL_MS = 12 * 60 * 60 * 1000
const PUSH_REALTIME_FALLBACK_SYNC_MS = 30_000
const ACCOUNT_STATE_EVENT = 'ql7:account-state'
const ACCOUNT_STATE_REFRESH_REQUEST_EVENT = 'ql7:account-state-refresh-request'
const DM_REALTIME_ELIGIBILITY_EVENT = 'ql7:dm-realtime-eligibility'
const COMMUNITY_REALTIME_ELIGIBILITY_EVENT = 'ql7:community-realtime-eligibility'
const PUSH_PULSE_MOBILE_FALLBACK_MS = 20_000
const PUSH_PULSE_DESKTOP_FALLBACK_MS = 60_000
const PUSH_PULSE_JITTER_MS = 1_000

function readCounts() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    return normalizeNotificationCounts(value)
  } catch {
    return normalizeNotificationCounts({})
  }
}

function readReadAt() {
  try {
    const value = JSON.parse(localStorage.getItem(READ_AT_STORAGE_KEY) || '{}')
    return value && typeof value === 'object' ? value : {}
  } catch {
    return {}
  }
}

function totalCounts(counts) {
  return totalNotificationCounts(counts)
}

function readAccountId() {
  try {
    const cookieAccount = String(document.cookie || '')
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith('asherId='))
      ?.slice('asherId='.length) || ''
    return String(
      window.__AUTH_ACCOUNT__ ||
      localStorage.getItem('asherId') ||
      localStorage.getItem('ql7_uid') ||
      localStorage.getItem('ql7_account') ||
      localStorage.getItem('account') ||
      localStorage.getItem('wallet') ||
      cookieAccount ||
      '',
    ).trim()
  } catch {
    return ''
  }
}

function toApplicationServerKey(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

function isInstalledAndroidShell() {
  try {
    return /^android-app:\/\/com\.quantuml7ai\.app(?:\.debug)?(?:\/|$)/i.test(String(document.referrer || ''))
  } catch {
    return false
  }
}

function isMobileNotificationRuntime() {
  try {
    const userAgent = String(navigator.userAgent || '')
    const isIPadOSDesktopMode = /macintosh/i.test(userAgent) && Number(navigator.maxTouchPoints || 0) > 1
    return (
      /android|iphone|ipad|ipod|mobile|crios|fxios|edgios/i.test(userAgent) ||
      isIPadOSDesktopMode ||
      isInstalledAndroidShell()
    )
  } catch {
    return false
  }
}

function isRealtimeNotificationRuntime() {
  try {
    if (typeof document !== 'undefined' && document.visibilityState && document.visibilityState !== 'visible') {
      return false
    }
  } catch {}
  return true
}

function nativeLinkAttemptIsFresh(accountId, lang) {
  try {
    const value = JSON.parse(localStorage.getItem(NATIVE_LINK_STORAGE_KEY) || 'null')
    const sameIdentity = String(value?.accountId || '') === String(accountId || '') &&
      String(value?.lang || '') === String(lang || '')
    if (sameIdentity && value?.linked === true) return true
    return (
      sameIdentity &&
      Date.now() - Number(value?.attemptedAt || 0) < NATIVE_LINK_RETRY_MS
    )
  } catch {
    return false
  }
}

async function sha256Base64Url(value) {
  try {
    const webCrypto = globalThis.crypto
    if (!webCrypto?.subtle || typeof TextEncoder === 'undefined') throw new Error('subtle_unavailable')
    const buffer = await webCrypto.subtle.digest('SHA-256', new TextEncoder().encode(String(value || '')))
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '')
  } catch {
    const raw = String(value || '')
    return `${raw.length}:${raw.slice(0, 16)}:${raw.slice(-16)}`
  }
}

function pushSubscriptionRegistrationIsFresh(accountId, fingerprint) {
  try {
    const value = JSON.parse(localStorage.getItem(PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY) || 'null')
    return (
      String(value?.accountId || '') === String(accountId || '') &&
      String(value?.fingerprint || '') === String(fingerprint || '') &&
      Date.now() - Number(value?.registeredAt || 0) < PUSH_SUBSCRIPTION_REGISTER_TTL_MS
    )
  } catch {
    return false
  }
}

function rememberPushSubscriptionRegistration(accountId, fingerprint) {
  try {
    localStorage.setItem(PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY, JSON.stringify({
      accountId: String(accountId || ''),
      fingerprint: String(fingerprint || ''),
      registeredAt: Date.now(),
    }))
  } catch {}
}

export default function AndroidNotificationBadgeSync() {
  const { lang } = useI18n()
  const countsRef = useRef({})
  const readAtRef = useRef({})
  const registrationRef = useRef(null)
  const pushDeliveryReadyRef = useRef(false)
  const accountIdRef = useRef('')
  const langRef = useRef(lang)
  const lastRefreshAtRef = useRef(0)
  const eventRefreshRef = useRef(null)

  useEffect(() => { langRef.current = lang }, [lang])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '')
    const result = String(params.get('ql7NativePush') || '').trim()
    if (!result) return
    window.__QL7_NATIVE_PUSH_LINK_RESULT__ = result
    const accountId = readAccountId()
    try {
      if (result === 'linked' && accountId) {
        localStorage.setItem(NATIVE_LINK_STORAGE_KEY, JSON.stringify({ accountId, lang, linked: true }))
      } else if (result !== 'linked') {
        localStorage.removeItem(NATIVE_LINK_STORAGE_KEY)
      }
      const url = new URL(window.location.href)
      url.searchParams.delete('ql7NativePush')
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    } catch {}
  }, [lang])

  useEffect(() => {
    const source = normalizeNotificationSource(
      new URLSearchParams(window.location.search || '').get(NOTICE_PARAM),
      '',
    )
    if (!source) return undefined

    const dispatchNotice = () => {
      if (window.__QL7_PENDING_NOTIFICATION_SOURCE__ !== source) return
      if (source === 'metamarket_gifts') {
        window.dispatchEvent(new CustomEvent('metamarket:open', {
          detail: { source: 'notification', initialMode: 'collections' },
        }))
      } else if (source === 'messenger_messages' || source === 'messenger_replies') {
        window.dispatchEvent(new CustomEvent('ql7:open-notification', {
          detail: { source },
        }))
      }
    }

    window.__QL7_PENDING_NOTIFICATION_SOURCE__ = source
    // Открытие ветки еще не является прочтением: счетчик снимают observers конкретного контента.
    // Хосты глобальных поповеров и форумный runtime монтируются асинхронно.
    const timers = [120, 500, 1200].map((delay) => window.setTimeout(dispatchNotice, delay))
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete(NOTICE_PARAM)
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    } catch {}
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [])

  const apiPost = useCallback(async (url, userId, payload) => {
    if (!userId) return null
    return fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-forum-user-id': userId,
        'x-auth-account-id': userId,
      },
      body: JSON.stringify({ accountId: userId, ...payload }),
      cache: 'no-store',
      keepalive: true,
    })
  }, [])

  const apiGet = useCallback(async (url, userId) => {
    if (!userId) return null
    return fetch(url, {
      headers: {
        'x-forum-user-id': userId,
        'x-auth-account-id': userId,
      },
      cache: 'no-store',
    })
  }, [])

  const ensureNativePushLink = useCallback(async (overrideAccountId = '') => {
    const accountId = String(overrideAccountId || readAccountId() || '').trim()
    if (!accountId || !isInstalledAndroidShell()) return false
    try {
      if (window.__QL7_NATIVE_PUSH_LINK_RESULT__ === 'linked') {
        localStorage.setItem(NATIVE_LINK_STORAGE_KEY, JSON.stringify({
          accountId,
          lang: langRef.current,
          linked: true,
        }))
        window.__QL7_NATIVE_PUSH_LINK_RESULT__ = ''
        return true
      }
      if (nativeLinkAttemptIsFresh(accountId, langRef.current)) return true
      const response = await apiPost('/api/push/native/link', accountId, { lang: langRef.current })
      const payload = await response?.json?.().catch(() => null)
      if (!response?.ok || !payload?.linkUrl?.startsWith('quantuml7ai://push-link?nonce=')) return false
      localStorage.setItem(NATIVE_LINK_STORAGE_KEY, JSON.stringify({
        accountId,
        lang: langRef.current,
        attemptedAt: Date.now(),
      }))
      window.location.assign(payload.linkUrl)
      return true
    } catch {
      return false
    }
  }, [apiPost])

  const ensurePushSubscription = useCallback(async (overrideAccountId = '') => {
    const registration = registrationRef.current
    const accountId = String(overrideAccountId || readAccountId() || '').trim()
    accountIdRef.current = accountId
    if (
      !registration?.pushManager ||
      !accountId ||
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted'
    ) {
      pushDeliveryReadyRef.current = false
      return null
    }

    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      const response = await fetch('/api/push/config', { cache: 'no-store' })
      const config = await response.json().catch(() => ({}))
      if (!config?.publicKey) {
        pushDeliveryReadyRef.current = false
        return null
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toApplicationServerKey(config.publicKey),
      })
    }
    const nativeShell = isInstalledAndroidShell()
    const payload = subscription.toJSON()
    const fingerprint = await sha256Base64Url(JSON.stringify({
      accountId,
      endpoint: payload?.endpoint || '',
      expirationTime: payload?.expirationTime || null,
      p256dh: payload?.keys?.p256dh || '',
      auth: payload?.keys?.auth || '',
      lang: langRef.current,
      nativeShell,
    }))
    if (pushSubscriptionRegistrationIsFresh(accountId, fingerprint)) {
      pushDeliveryReadyRef.current = true
      return subscription
    }
    const response = await apiPost('/api/push/subscribe', accountId, {
      lang: langRef.current,
      nativeShell,
      subscription: payload,
    })
    pushDeliveryReadyRef.current = response?.ok === true
    if (response?.ok) rememberPushSubscriptionRegistration(accountId, fingerprint)
    return subscription
  }, [apiPost])

  const sync = useCallback(async () => {
    const counts = normalizeNotificationCounts(countsRef.current)
    const count = totalCounts(counts)
    const sources = NOTIFICATION_SOURCE_LIST.map((source) => (
      buildNotificationDescriptor(source, counts[source], langRef.current)
    ))
    try {
      if (typeof navigator.setAppBadge === 'function') {
        if (count > 0) await navigator.setAppBadge(count)
        else if (typeof navigator.clearAppBadge === 'function') await navigator.clearAppBadge()
      }
    } catch {}

    const registration = registrationRef.current
    const worker = registration?.active || registration?.waiting || registration?.installing
    if (worker && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      worker.postMessage({ type: 'ql7:badge-sync', sources, totalCount: count })
    }
  }, [])

  const applyServerState = useCallback((rawState) => {
    if (!rawState || rawState?.ok === false) return
    // Серверное состояние канонично: одинаковый аккаунт получает один badge на всех устройствах.
    countsRef.current = normalizeNotificationCounts(rawState?.counts || {})
    readAtRef.current = rawState?.readAt && typeof rawState.readAt === 'object'
      ? rawState.readAt
      : {}
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(countsRef.current))
      localStorage.setItem(READ_AT_STORAGE_KEY, JSON.stringify(readAtRef.current))
    } catch {}
    window.__QL7_NOTIFICATION_STATE__ = {
      counts: countsRef.current,
      readAt: readAtRef.current,
      version: Math.max(0, Number(rawState?.version) || 0),
      readItems: rawState?.readItems && typeof rawState.readItems === 'object'
        ? rawState.readItems
        : {},
      totalCount: totalCounts(countsRef.current),
    }
    window.dispatchEvent(new CustomEvent('ql7:notification-state', {
      detail: window.__QL7_NOTIFICATION_STATE__,
    }))
    if (rawState?.accountState && typeof rawState.accountState === 'object') {
      window.__QL7_ACCOUNT_STATE__ = rawState.accountState
      window.dispatchEvent(new CustomEvent(ACCOUNT_STATE_EVENT, {
        detail: rawState.accountState,
      }))
    }
    sync()
  }, [sync])

  const fetchServerState = useCallback(async (overrideAccountId = '') => {
    const accountId = String(overrideAccountId || accountIdRef.current || readAccountId() || '').trim()
    if (!accountId) return null
    accountIdRef.current = accountId
    const response = await apiGet('/api/push/sync', accountId)
    const state = await response?.json?.().catch(() => null)
    if (response?.ok && state?.ok) {
      lastRefreshAtRef.current = Date.now()
      applyServerState(state)
    }
    return state
  }, [apiGet, applyServerState])

  const refreshFromNotificationImpulse = useCallback(({ force = false } = {}) => {
    const now = Date.now()
    if (!force && now - Number(lastRefreshAtRef.current || 0) < 3000) return Promise.resolve(null)
    if (eventRefreshRef.current) return eventRefreshRef.current
    const request = fetchServerState()
      .finally(() => {
        if (eventRefreshRef.current === request) eventRefreshRef.current = null
      })
    eventRefreshRef.current = request
    return request
  }, [fetchServerState])

  const markSourceRead = useCallback(async (sourceRaw) => {
    const source = normalizeNotificationSource(sourceRaw, '')
    const accountId = accountIdRef.current || readAccountId()
    if (!source || !accountId) return null
    countsRef.current = normalizeNotificationCounts({ ...countsRef.current, [source]: 0 })
    sync()
    const response = await apiPost('/api/push/sync', accountId, {
      readSources: [source],
      readAt: Date.now(),
    })
    const state = await response?.json?.().catch(() => null)
    if (response?.ok && state?.ok) applyServerState(state)
    return state
  }, [apiPost, applyServerState, sync])

  useEffect(() => {
    const serviceWorkerSupported = 'serviceWorker' in navigator
    countsRef.current = readCounts()
    readAtRef.current = readReadAt()
    let cancelled = false
    let mobileStreamAbort = null
    let mobileStreamRetry = null
    let mobileStreamAttempt = 0
    let mobileFallbackSync = null
    let mobileRealtimeHealthy = true
    let dmRealtimeStreamHealthy = false
    let dmRealtimeEligible = window.__QL7_DM_REALTIME_ELIGIBLE__ === true
    let communityRealtimeId = String(window.__QL7_COMMUNITY_REALTIME_ID__ || '').trim()
    let impulseLeader = null
    let impulseLeaderAccountId = ''
    let pulseLeader = null
    let pulseLeaderAccountId = ''
    let pulseTimer = null
    let pulseInFlight = null
    let pulseLastAt = 0
    let pulseSystemSupportRevision = 0
    let pulseAccountStateRevision = 0

    const onBrowserNotificationsAcknowledged = (event) => {
      const state = event?.detail && typeof event.detail === 'object' ? event.detail : null
      if (state?.counts) applyServerState(state)
      else {
        countsRef.current = normalizeNotificationCounts({})
        sync()
      }
    }

    const stopFallbackCanonicalSync = () => {
      if (mobileFallbackSync) window.clearTimeout(mobileFallbackSync)
      mobileFallbackSync = null
    }

    const scheduleFallbackCanonicalSync = () => {
      stopFallbackCanonicalSync()
      if (cancelled || mobileRealtimeHealthy || !impulseLeader?.isLeader?.()) return
      mobileFallbackSync = window.setTimeout(() => {
        mobileFallbackSync = null
        if (cancelled || mobileRealtimeHealthy || !impulseLeader?.isLeader?.()) return
        fetchServerState()
          .catch(() => null)
          .finally(() => scheduleFallbackCanonicalSync())
      }, PUSH_REALTIME_FALLBACK_SYNC_MS)
    }

    const stopMobileImpulseStream = () => {
      if (mobileStreamRetry) window.clearTimeout(mobileStreamRetry)
      mobileStreamRetry = null
      stopFallbackCanonicalSync()
      mobileStreamAbort?.abort()
      mobileStreamAbort = null
    }

    const shouldUseForegroundImpulseStream = () => {
      if (cancelled || (!dmRealtimeEligible && !communityRealtimeId) || !isRealtimeNotificationRuntime()) return false
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) return false
      try {
        if (document.visibilityState && document.visibilityState !== 'visible') return false
      } catch {}
      return true
    }

    const dispatchImpulseFallback = (payload, state = null) => {
      const source = normalizeNotificationSource(payload?.source, '')
      if (!source) return
      const serverCounts = state?.counts && typeof state.counts === 'object' ? state.counts : null
      window.dispatchEvent(new CustomEvent(SOURCE_EVENT, {
        detail: {
          source,
          count: Math.max(0, Number(serverCounts?.[source] ?? payload?.count) || 0),
          totalCount: Math.max(0, Number(state?.totalCount ?? payload?.totalCount) || 0),
          pushReceived: true,
          forceSync: payload?.forceSync === true,
          clientOnly: payload?.forceSync !== true,
          reason: String(payload?.reason || ''),
          version: Math.max(0, Number(state?.version ?? payload?.version) || 0),
          dmDeletedMessageId: String(payload?.dmDeletedMessageId || ''),
          dmDeletedPeerId: String(payload?.dmDeletedPeerId || ''),
          dmDeletedDialog: payload?.dmDeletedDialog === true,
        },
      }))
    }

    const handleNotificationImpulsePayload = (payload) => {
      if (payload?.type === 'community-event') {
        const detail = payload?.event && typeof payload.event === 'object' ? payload.event : null
        if (detail?.type) {
          window.dispatchEvent(new CustomEvent('ql7:forum-event', {
            detail: { ...detail, realtimeTransport: 'notification-stream' },
          }))
        }
        return
      }
      if (payload?.type === 'notification-realtime-health') {
        if (communityRealtimeId && String(payload?.communityId || '') !== communityRealtimeId) return
        mobileRealtimeHealthy = payload?.ok === true
        dmRealtimeStreamHealthy = dmRealtimeEligible && mobileRealtimeHealthy
        syncForegroundImpulseOwnership()
        if (mobileRealtimeHealthy) {
          stopFallbackCanonicalSync()
          return
        }
        refreshFromNotificationImpulse().catch(() => {})
        scheduleFallbackCanonicalSync()
        return
      }
      if (payload?.type === 'notification-stream-rotate') return
      if (payload?.type !== 'notification-state-changed') return
      if (payload?.systemSupportChanged === true) {
        const supportRevision = Math.max(0, Number(payload?.supportRevision) || 0)
        if (supportRevision > 0 && supportRevision <= pulseSystemSupportRevision) return
        if (supportRevision > 0) pulseSystemSupportRevision = supportRevision
        window.dispatchEvent(new CustomEvent('ql7:system-support:refresh', {
          detail: { reason: String(payload?.reason || 'system_support_changed'), supportRevision },
        }))
        return
      }
      if (payload?.accountStateChanged === true) {
        const accountStateRevision = Math.max(0, Number(payload?.accountStateRevision) || 0)
        if (accountStateRevision > 0 && accountStateRevision <= pulseAccountStateRevision) return
        if (accountStateRevision > 0) pulseAccountStateRevision = accountStateRevision
        refreshFromNotificationImpulse({ force: true }).catch(() => {})
        return
      }
      const exactImpulse = Number.isFinite(Number(payload?.count)) &&
        Number.isFinite(Number(payload?.totalCount)) &&
        payload?.forceSync !== true
      if (exactImpulse) {
        dispatchImpulseFallback(payload)
        return
      }
      refreshFromNotificationImpulse({ force: payload?.forceSync === true })
        .then((state) => {
          if (!state?.ok || payload?.forceSync === true) dispatchImpulseFallback(payload, state)
        })
        .catch(() => dispatchImpulseFallback(payload))
    }

    const disposeImpulseLeader = () => {
      impulseLeader?.stop?.()
      impulseLeader = null
      impulseLeaderAccountId = ''
    }

    const ensureImpulseLeader = (accountIdRaw = '') => {
      const accountId = String(accountIdRaw || accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) {
        disposeImpulseLeader()
        return null
      }
      const accountScope = accountId.toLowerCase()
      if (impulseLeader && impulseLeaderAccountId === accountScope) return impulseLeader
      disposeImpulseLeader()
      impulseLeaderAccountId = accountScope
      impulseLeader = createNotificationImpulseLeader({
        accountId,
        onLeadershipChange: (isLeader) => {
          if (cancelled) return
          if (isLeader && shouldUseForegroundImpulseStream()) startMobileImpulseStream()
          else stopMobileImpulseStream()
        },
        onImpulse: handleNotificationImpulsePayload,
      })
      return impulseLeader
    }

    const stopPulseScheduler = () => {
      if (pulseTimer) window.clearTimeout(pulseTimer)
      pulseTimer = null
    }

    const backgroundPushLikelyReady = () => {
      try {
        return pushDeliveryReadyRef.current === true &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted' &&
          !!registrationRef.current?.pushManager
      } catch {
        return false
      }
    }

    // Only the global notification SSE can replace this reconciliation channel.
    // Battle Chat SSE carries Battle Chat data only and must never suppress global
    // Replies/System Support delivery. Native FCM is background delivery and does
    // not bridge live state into the WebView, so a visible mobile shell keeps pulse.
    const realtimeOwnsPulse = () => (dmRealtimeEligible && dmRealtimeStreamHealthy)

    const pulseCadenceMs = () => {
      if (isMobileNotificationRuntime()) return PUSH_PULSE_MOBILE_FALLBACK_MS
      return backgroundPushLikelyReady()
        ? PUSH_PULSE_DESKTOP_FALLBACK_MS
        : PUSH_PULSE_MOBILE_FALLBACK_MS
    }

    const shouldUsePulseFallback = () => {
      if (realtimeOwnsPulse()) return false
      // A valid desktop PushSubscription only proves that the browser accepted
      // registration. The OS/browser can still suppress delivery afterwards.
      // Keep one low-frequency, leader-elected Redis pulse as a delivery
      // watchdog on desktop; degraded desktop delivery uses the mobile cadence.
      if (isMobileNotificationRuntime() && backgroundPushLikelyReady()) return false
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) return false
      try {
        if (document.visibilityState && document.visibilityState !== 'visible') return false
      } catch {}
      return true
    }

    const pulseDelay = () => (shouldUsePulseFallback() ? pulseCadenceMs() : null)

    const applyPulseSystemSupportRevision = (pulse) => {
      const nextRevision = Math.max(0, Number(pulse?.supportRevision) || 0)
      if (nextRevision === pulseSystemSupportRevision) return false
      pulseSystemSupportRevision = nextRevision
      if (nextRevision <= 0) return false
      window.dispatchEvent(new CustomEvent('ql7:system-support:refresh', {
        detail: { reason: 'push_pulse_system_support_revision', supportRevision: nextRevision },
      }))
      return true
    }

    const applyPulseAccountStateRevision = (pulse) => {
      const nextRevision = Math.max(0, Number(pulse?.accountStateRevision) || 0)
      if (nextRevision === pulseAccountStateRevision) return false
      pulseAccountStateRevision = nextRevision
      if (nextRevision <= 0) return false
      refreshFromNotificationImpulse({ force: true }).catch(() => {})
      return true
    }

    const applyPulseSnapshot = (pulse) => {
      if (!pulse?.available) return false
      const supportChanged = applyPulseSystemSupportRevision(pulse)
      const accountStateChanged = applyPulseAccountStateRevision(pulse)
      if (!pulse?.counts || typeof pulse.counts !== 'object') return supportChanged || accountStateChanged
      const incomingVersion = Math.max(0, Number(pulse?.version) || 0)
      const currentState = window.__QL7_NOTIFICATION_STATE__ && typeof window.__QL7_NOTIFICATION_STATE__ === 'object'
        ? window.__QL7_NOTIFICATION_STATE__
        : {}
      const currentVersion = Math.max(0, Number(currentState?.version) || 0)
      if (currentVersion > 0 && incomingVersion < currentVersion) return supportChanged || accountStateChanged
      const nextCounts = normalizeNotificationCounts(pulse.counts)
      const unchanged = NOTIFICATION_SOURCE_LIST.every((source) => (
        Math.max(0, Number(countsRef.current?.[source]) || 0) === Math.max(0, Number(nextCounts?.[source]) || 0)
      )) && incomingVersion === currentVersion
      if (unchanged) return supportChanged || accountStateChanged
      countsRef.current = nextCounts
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(nextCounts)) } catch {}
      window.__QL7_NOTIFICATION_STATE__ = {
        ...currentState,
        counts: nextCounts,
        readAt: currentState?.readAt || readAtRef.current || {},
        readItems: currentState?.readItems || {},
        totalCount: totalCounts(nextCounts),
        version: Math.max(currentVersion, incomingVersion),
      }
      window.dispatchEvent(new CustomEvent('ql7:notification-state', {
        detail: window.__QL7_NOTIFICATION_STATE__,
      }))
      sync()
      return true
    }

    const schedulePulse = (delayOverride = null) => {
      if (cancelled || !pulseLeader?.isLeader?.() || !shouldUsePulseFallback()) return
      if (pulseTimer) return
      const cadence = pulseCadenceMs()
      const cooldown = pulseLastAt > 0
        ? Math.max(0, cadence - (Date.now() - pulseLastAt))
        : 0
      const base = delayOverride == null
        ? pulseDelay()
        : Math.max(cooldown, Math.max(0, Number(delayOverride) || 0))
      if (!Number.isFinite(Number(base))) return
      const jitter = delayOverride == null ? Math.floor(Math.random() * PUSH_PULSE_JITTER_MS) : 0
      pulseTimer = window.setTimeout(() => {
        pulseTimer = null
        runPulse().catch(() => {})
      }, Number(base) + jitter)
    }

    const runPulse = async ({ canonicalOnMiss = false } = {}) => {
      if (cancelled || !pulseLeader?.isLeader?.() || !shouldUsePulseFallback()) return null
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) return null
      if (pulseInFlight) return pulseInFlight
      pulseInFlight = (async () => {
        const response = await apiGet('/api/push/pulse', accountId)
        const pulse = await response?.json?.().catch(() => null)
        pulseLastAt = Date.now()
        if (response?.ok && pulse?.ok) {
          applyPulseSnapshot(pulse)
          pulseLeader?.publishImpulse?.(pulse)
          if (!pulse?.available && canonicalOnMiss) await fetchServerState(accountId).catch(() => {})
        } else if (canonicalOnMiss) {
          await fetchServerState(accountId).catch(() => {})
        }
        return pulse
      })().finally(() => {
        pulseInFlight = null
        schedulePulse()
      })
      return pulseInFlight
    }

    const disposePulseLeader = () => {
      pulseLeader?.stop?.()
      pulseLeader = null
      pulseLeaderAccountId = ''
      stopPulseScheduler()
    }

    const ensurePulseLeader = (accountIdRaw = '') => {
      const accountId = String(accountIdRaw || accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) {
        disposePulseLeader()
        return null
      }
      const accountScope = `${accountId.toLowerCase()}::pulse`
      if (pulseLeader && pulseLeaderAccountId === accountScope) return pulseLeader
      disposePulseLeader()
      pulseLeaderAccountId = accountScope
      pulseLeader = createNotificationImpulseLeader({
        accountId: accountScope,
        onLeadershipChange: (isLeader) => {
          if (cancelled) return
          if (isLeader) schedulePulse(1200)
          else stopPulseScheduler()
        },
        onImpulse: applyPulseSnapshot,
      })
      return pulseLeader
    }

    const syncForegroundImpulseOwnership = () => {
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      let visible = true
      try { visible = !document.visibilityState || document.visibilityState === 'visible' } catch {}
      if (cancelled || !accountId || !visible) {
        impulseLeader?.setEligible?.(false)
        pulseLeader?.setEligible?.(false)
        stopMobileImpulseStream()
        stopPulseScheduler()
        return
      }

      const pulseEligible = shouldUsePulseFallback()
      if (!pulseEligible) {
        pulseLeader?.setEligible?.(false)
        stopPulseScheduler()
      } else {
        const pulseCoordinator = ensurePulseLeader(accountId)
        pulseCoordinator?.setEligible?.(true)
        if (pulseCoordinator?.isLeader?.()) schedulePulse()
      }

      if (!shouldUseForegroundImpulseStream()) {
        impulseLeader?.setEligible?.(false)
        stopMobileImpulseStream()
        return
      }
      const coordinator = ensureImpulseLeader(accountId)
      coordinator?.setEligible?.(true)
      if (coordinator?.isLeader?.()) startMobileImpulseStream()
    }

    const onDmRealtimeEligibility = (event) => {
      dmRealtimeEligible = event?.detail?.active === true
      window.__QL7_DM_REALTIME_ELIGIBLE__ = dmRealtimeEligible
      if (!dmRealtimeEligible) {
        dmRealtimeStreamHealthy = false
        stopMobileImpulseStream()
      }
      syncForegroundImpulseOwnership()
      if (!dmRealtimeEligible && pulseLeader?.isLeader?.()) schedulePulse(600)
    }

    const onCommunityRealtimeEligibility = (event) => {
      const nextCommunityId = event?.detail?.active === true
        ? String(event?.detail?.communityId || '').trim()
        : ''
      if (nextCommunityId === communityRealtimeId) return
      communityRealtimeId = nextCommunityId
      window.__QL7_COMMUNITY_REALTIME_ID__ = communityRealtimeId
      stopMobileImpulseStream()
      syncForegroundImpulseOwnership()
      if (impulseLeader?.isLeader?.() && shouldUseForegroundImpulseStream()) scheduleMobileImpulseStream(0)
    }

    function scheduleMobileImpulseStream(delayOverride = null) {
      if (!shouldUseForegroundImpulseStream()) {
        stopMobileImpulseStream()
        return
      }
      if (!impulseLeader?.isLeader?.()) return
      if (mobileStreamRetry) window.clearTimeout(mobileStreamRetry)
      const delay = Number.isFinite(Number(delayOverride))
        ? Math.max(0, Number(delayOverride))
        : Math.min(15_000, 1_000 * (2 ** Math.min(mobileStreamAttempt, 4)))
      mobileStreamRetry = window.setTimeout(() => {
        mobileStreamRetry = null
        startMobileImpulseStream()
      }, delay)
    }

    function startMobileImpulseStream() {
      if (!shouldUseForegroundImpulseStream()) {
        stopMobileImpulseStream()
        return
      }
      if (!impulseLeader?.isLeader?.()) return
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      if (!accountId || mobileStreamAbort) return

      const controller = new AbortController()
      mobileStreamAbort = controller
      let streamFailed = false
      const streamOptions = {
        headers: {
          accept: 'text/event-stream',
          'x-forum-user-id': accountId,
          'x-auth-account-id': accountId,
        },
        cache: 'no-store',
        signal: controller.signal,
      }
      const streamRequest = communityRealtimeId
        ? fetch(`/api/push/events?communityId=${encodeURIComponent(communityRealtimeId)}`, streamOptions)
        : fetch('/api/push/events', streamOptions)
      streamRequest
        .then(async (response) => {
          if ((response.status === 403 || response.status === 404) && communityRealtimeId) {
            const revokedCommunityId = communityRealtimeId
            communityRealtimeId = ''
            window.__QL7_COMMUNITY_REALTIME_ID__ = ''
            window.dispatchEvent(new CustomEvent('ql7:community-access-revoked', {
              detail: { communityId: revokedCommunityId, status: response.status },
            }))
            return
          }
          if (!response.ok || !response.body) throw new Error('push_event_stream_unavailable')
          mobileStreamAttempt = 0
          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          while (!cancelled && !controller.signal.aborted) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const packets = buffer.split('\n\n')
            buffer = packets.pop() || ''
            packets.forEach((packet) => {
              const data = packet
                .split('\n')
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.slice(5).trim())
                .join('\n')
              if (!data) return
              try {
                const payload = JSON.parse(data)
                if (payload?.type !== 'notification-state-changed' &&
                    payload?.type !== 'community-event' &&
                    payload?.type !== 'notification-realtime-health' &&
                    payload?.type !== 'notification-stream-rotate') return
                handleNotificationImpulsePayload(payload)
                impulseLeader?.publishImpulse?.(payload)
              } catch {}
            })
          }
        })
        .catch(() => {
          streamFailed = true
          mobileRealtimeHealthy = false
          dmRealtimeStreamHealthy = false
          syncForegroundImpulseOwnership()
          refreshFromNotificationImpulse().catch(() => {})
        })
        .finally(() => {
          if (mobileStreamAbort === controller) mobileStreamAbort = null
          if (!cancelled && !controller.signal.aborted && impulseLeader?.isLeader?.()) {
            if (streamFailed) {
              scheduleFallbackCanonicalSync()
              mobileStreamAttempt += 1
              scheduleMobileImpulseStream()
            } else {
              mobileStreamAttempt = 0
              scheduleMobileImpulseStream(250)
            }
          }
        })
    }

    syncForegroundImpulseOwnership()
    // Bootstrap canonical notification + account restriction state once through
    // the unified sync route. Service-worker availability must not decide whether
    // account safety state is loaded.
    fetchServerState().catch(() => sync())
    if (serviceWorkerSupported) {
      navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' })
        .then((registration) => {
          if (cancelled) return
          registrationRef.current = registration
          registration.update().catch(() => {})
          accountIdRef.current = readAccountId()
          ensurePushSubscription()
            .catch(() => null)
            .finally(() => syncForegroundImpulseOwnership())
          ensureNativePushLink().catch(() => {})
          syncForegroundImpulseOwnership()
        })
        .catch(() => {})
    }

    const onCount = (event) => {
      const source = normalizeNotificationSource(event?.detail?.source, '')
      if (!source) return
      const previousCount = Math.max(0, Number(countsRef.current?.[source]) || 0)
      const nextCount = Math.max(0, Number(event?.detail?.count) || 0)
      const explicitRead = event?.detail?.read === true
      const forceSync = event?.detail?.forceSync === true
      const clientOnly = event?.detail?.clientOnly === true || event?.detail?.localUnreadProjection === true
      const incomingVersion = Math.max(0, Number(event?.detail?.version) || 0)
      const currentVersion = Math.max(0, Number(window.__QL7_NOTIFICATION_STATE__?.version) || 0)
      if (!forceSync && incomingVersion > 0 && currentVersion > 0 && incomingVersion < currentVersion) return
      if (nextCount === previousCount && !explicitRead && !forceSync) return
      if (forceSync && !explicitRead && nextCount <= previousCount) {
        sync()
        return
      }
      countsRef.current = normalizeNotificationCounts({
        ...countsRef.current,
        [source]: nextCount,
      })
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(countsRef.current)) } catch {}
      window.__QL7_NOTIFICATION_STATE__ = {
        counts: countsRef.current,
        readAt: readAtRef.current,
        readItems: window.__QL7_NOTIFICATION_STATE__?.readItems &&
          typeof window.__QL7_NOTIFICATION_STATE__.readItems === 'object'
          ? window.__QL7_NOTIFICATION_STATE__.readItems
          : {},
        totalCount: totalCounts(countsRef.current),
        version: Math.max(0, Number(event?.detail?.version) || Number(window.__QL7_NOTIFICATION_STATE__?.version) || 0),
      }
      window.dispatchEvent(new CustomEvent('ql7:notification-state', {
        detail: window.__QL7_NOTIFICATION_STATE__,
      }))
      sync()
      if (clientOnly || forceSync) return
      if (source === 'messenger_messages') {
        // DM read receipts are owned by /api/dm/seen after a real message row
        // enters the open thread viewport. Dialog-list projections must never
        // collapse the canonical unread state by writing readSources here.
        if (nextCount > 0) {
          apiPost('/api/push/sync', accountIdRef.current || readAccountId(), {
            counts: { [source]: nextCount },
          })
            .then((response) => response?.json?.().catch(() => null))
            .then((state) => {
              if (state?.ok) applyServerState(state)
            })
            .catch(() => {})
        }
        return
      }
      const isCanonicalForumReplies = source === 'messenger_replies' && event?.detail?.canonicalForumUnread === true
      const isServerReplyReadSync = source === 'messenger_replies' && event?.detail?.serverItemsRead === true
      // readItems already completed an authoritative /api/push/sync POST and returned
      // the canonical count. Re-posting that count here only duplicates Vercel/Mongo work.
      if (isServerReplyReadSync) return
      if (source === 'messenger_replies' && isCanonicalForumReplies) {
        apiPost('/api/push/sync', accountIdRef.current || readAccountId(), {
          counts: { [source]: nextCount },
        })
          .then((response) => response?.json?.().catch(() => null))
          .then((state) => {
            if (state?.ok) applyServerState(state)
          })
          .catch(() => {})
        return
      }
      if (source === 'messenger_replies') return
      if ((nextCount <= 0 && previousCount > 0) || explicitRead) {
        markSourceRead(source).catch(() => {})
      } else {
        apiPost('/api/push/sync', accountIdRef.current || readAccountId(), {
          counts: { [source]: nextCount },
        })
          .then((response) => response?.json?.().catch(() => null))
          .then((state) => {
            if (state?.ok) applyServerState(state)
          })
          .catch(() => {})
      }
    }

    const acknowledgeNotificationClick = async () => {
      let shouldAck = false
      try {
        const url = new URL(window.location.href)
        shouldAck = url.searchParams.get('ql7PushAck') === '1'
        if (shouldAck) {
          url.searchParams.delete('ql7PushAck')
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
        }
      } catch {}
      if (!shouldAck) return
      const accountId = String(accountIdRef.current || readAccountId() || '').trim()
      if (!accountId) return
      try {
        const response = await apiPost('/api/push/sync', accountId, {
          readSources: NOTIFICATION_SOURCE_LIST,
          readAt: Date.now(),
        })
        const state = await response?.json?.().catch(() => null)
        if (response?.ok && state?.ok) applyServerState(state)
      } catch {}
    }

    const onWorkerMessage = (event) => {
      if (event?.data?.type !== 'ql7:notification-received') return
      if (event?.data?.systemSupportChanged === true || event?.data?.accountStateChanged === true) {
        handleNotificationImpulsePayload({
          ...event.data,
          type: 'notification-state-changed',
        })
        return
      }
      const source = normalizeNotificationSource(event?.data?.source, '')
      if (!source) return
      const exactImpulse = Number.isFinite(Number(event?.data?.count)) &&
        Number.isFinite(Number(event?.data?.totalCount)) &&
        event?.data?.forceSync !== true
      if (exactImpulse) {
        dispatchImpulseFallback(event.data)
        return
      }
      refreshFromNotificationImpulse({ force: event?.data?.forceSync === true }).then((state) => {
        if (state?.ok) return
        dispatchImpulseFallback(event.data)
      }).catch(() => dispatchImpulseFallback(event.data))
    }

    const requestPermissionOnce = () => {
      if (typeof Notification === 'undefined' || Notification.permission !== 'default') return
      const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
      const android = /Android/i.test(String(navigator.userAgent || ''))
      if (!standalone && !android) return
      if (!readAccountId()) return
      Notification.requestPermission()
        .then(() => ensurePushSubscription())
        .then(() => fetchServerState())
        .catch(() => {})
    }

    const onAuth = (event) => {
      const accountId = String(event?.detail?.accountId || readAccountId() || '').trim()
      accountIdRef.current = accountId
      const permissionRequest = (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default' &&
        (/Android/i.test(String(navigator.userAgent || '')) ||
          window.matchMedia?.('(display-mode: standalone)')?.matches)
      )
        ? Notification.requestPermission().catch(() => 'default')
        : Promise.resolve(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission)
      permissionRequest
        .then(() => ensurePushSubscription(accountId))
        .then(() => ensureNativePushLink(accountId))
        .then(() => fetchServerState(accountId))
        .then(() => syncForegroundImpulseOwnership())
        .catch(() => {})
    }
    const onLogout = () => {
      const accountId = accountIdRef.current || readAccountId()
      const registration = registrationRef.current
      countsRef.current = normalizeNotificationCounts({})
      readAtRef.current = {}
      try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(READ_AT_STORAGE_KEY)
        localStorage.removeItem(NATIVE_LINK_STORAGE_KEY)
        localStorage.removeItem(PUSH_SUBSCRIPTION_FINGERPRINT_STORAGE_KEY)
      } catch {}
      window.__QL7_NOTIFICATION_STATE__ = {
        counts: countsRef.current,
        readAt: readAtRef.current,
        totalCount: 0,
      }
      window.dispatchEvent(new CustomEvent('ql7:notification-state', {
        detail: window.__QL7_NOTIFICATION_STATE__,
      }))
      window.__QL7_ACCOUNT_STATE__ = null
      window.dispatchEvent(new CustomEvent(ACCOUNT_STATE_EVENT, { detail: null }))
      sync()
      registration?.pushManager?.getSubscription?.()
        .then(async (subscription) => {
          if (!subscription) return
          await apiPost('/api/push/unsubscribe', accountId, { endpoint: subscription.endpoint }).catch(() => {})
          await subscription.unsubscribe().catch(() => {})
        })
        .catch(() => {})
      if (isInstalledAndroidShell()) {
        window.setTimeout(() => window.location.assign('quantuml7ai://push-unlink'), 0)
      }
      accountIdRef.current = ''
      pushDeliveryReadyRef.current = false
      disposeImpulseLeader()
      disposePulseLeader()
      stopMobileImpulseStream()
      stopPulseScheduler()
    }

    const refreshCanonicalState = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        impulseLeader?.setEligible?.(false)
        pulseLeader?.setEligible?.(false)
        stopMobileImpulseStream()
        stopPulseScheduler()
        return
      }
      const now = Date.now()
      if (now - lastRefreshAtRef.current < 5000) {
        syncForegroundImpulseOwnership()
        return
      }
      lastRefreshAtRef.current = now
      // Restore background delivery, then prefer the Redis-only pulse.
      // Canonical /push/sync is recovery-only when the runtime snapshot is absent.
      ensurePushSubscription()
        .then(() => ensureNativePushLink())
        .then(() => runPulse({ canonicalOnMiss: true }))
        .then(() => syncForegroundImpulseOwnership())
        .catch(() => fetchServerState().catch(() => {}))
      syncForegroundImpulseOwnership()
    }

    const onAccountStateRefreshRequest = () => {
      refreshFromNotificationImpulse({ force: true }).catch(() => {})
    }

    window.addEventListener(SOURCE_EVENT, onCount)
    window.addEventListener(DM_REALTIME_ELIGIBILITY_EVENT, onDmRealtimeEligibility)
    window.addEventListener(COMMUNITY_REALTIME_ELIGIBILITY_EVENT, onCommunityRealtimeEligibility)
    window.addEventListener(ACCOUNT_STATE_REFRESH_REQUEST_EVENT, onAccountStateRefreshRequest)
    window.addEventListener('ql7:browser-notifications-acknowledged', onBrowserNotificationsAcknowledged)
    if (serviceWorkerSupported) navigator.serviceWorker.addEventListener('message', onWorkerMessage)
    void acknowledgeNotificationClick()
    window.addEventListener('auth:ok', onAuth)
    window.addEventListener('auth:success', onAuth)
    window.addEventListener('auth:logout', onLogout)
    window.addEventListener('focus', refreshCanonicalState)
    document.addEventListener('visibilitychange', refreshCanonicalState)
    window.addEventListener('pointerdown', requestPermissionOnce, { passive: true })
    return () => {
      cancelled = true
      disposeImpulseLeader()
      disposePulseLeader()
      stopMobileImpulseStream()
      stopPulseScheduler()
      window.removeEventListener(SOURCE_EVENT, onCount)
      window.removeEventListener(DM_REALTIME_ELIGIBILITY_EVENT, onDmRealtimeEligibility)
      window.removeEventListener(COMMUNITY_REALTIME_ELIGIBILITY_EVENT, onCommunityRealtimeEligibility)
      window.removeEventListener(ACCOUNT_STATE_REFRESH_REQUEST_EVENT, onAccountStateRefreshRequest)
      window.removeEventListener('ql7:browser-notifications-acknowledged', onBrowserNotificationsAcknowledged)
      if (serviceWorkerSupported) navigator.serviceWorker.removeEventListener('message', onWorkerMessage)
      window.removeEventListener('auth:ok', onAuth)
      window.removeEventListener('auth:success', onAuth)
      window.removeEventListener('auth:logout', onLogout)
      window.removeEventListener('focus', refreshCanonicalState)
      document.removeEventListener('visibilitychange', refreshCanonicalState)
      window.removeEventListener('pointerdown', requestPermissionOnce)
    }
  }, [apiGet, apiPost, applyServerState, ensureNativePushLink, ensurePushSubscription, fetchServerState, markSourceRead, refreshFromNotificationImpulse, sync])

  return null
}
